import React from "react";
import { Link } from "react-router-dom";

/**
 * Full-page wizard hero shown when there are no databases or no results yet.
 *
 * Props:
 *   wizardStep          – null | 'create-db-name' | 'create-db-confirm' | 'create-db-desc'
 *   wizardDbName        – string
 *   wizardDbError       – string
 *   wizardMessages      – Array<{ id, role, text }>
 *   wizardInput         – string
 *   onWizardInputChange – (e) => void
 *   assistantLoading    – bool
 *   assistantError      – string
 *   creatingDb          – bool
 *   aiEnabled           – bool
 *   wizardFlag          – bool  (true when ?wizard=1 is in the URL)
 *   onSubmit            – (e) => void  (form submit handler)
 *   onConfirmYes        – () => void   (button: "Looks good")
 *   onConfirmChange     – () => void   (button: "Change name")
 *   onSkipDesc          – () => void   (button: "Skip description")
 *   onCreateNow         – () => void   (button: "Create database now")
 *   onCancel            – () => void   (button: "Cancel setup")
 *   onStartWizard       – () => void   (button: "Create database" on step card 1)
 *   t                   – i18next translation function
 */
export default function WizardHero({
  wizardStep,
  wizardDbName,
  wizardDbError,
  wizardMessages,
  wizardInput,
  onWizardInputChange,
  assistantLoading,
  assistantError,
  creatingDb,
  aiEnabled,
  wizardFlag,
  onSubmit,
  onConfirmYes,
  onConfirmChange,
  onSkipDesc,
  onCreateNow,
  onCancel,
  onStartWizard,
  t,
}) {
  return (
    <div className="wizard-hero">
      <div className="wizard-orb3d-wrap" aria-hidden="true">
        <div className="wizard-orb3d-halo" />
        <div className="wizard-orb3d-shell" />
        <svg
          className="wizard-cosmos-svg"
          viewBox="0 0 240 240"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <clipPath id="wizardCosmosClip">
              <circle cx="120" cy="120" r="116" />
            </clipPath>
            <radialGradient id="wizNebA" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#bfdbfe" stopOpacity="0.55" />
              <stop offset="45%" stopColor="#3b82f6" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizNebB" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#e9d5ff" stopOpacity="0.45" />
              <stop offset="45%" stopColor="#a855f7" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#581c87" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizNebC" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#fbcfe8" stopOpacity="0.4" />
              <stop offset="45%" stopColor="#ec4899" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#831843" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizNebD" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#a5f3fc" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#164e63" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizGalCenter" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#ffffff" stopOpacity="1" />
              <stop offset="18%" stopColor="#dbeafe" stopOpacity="0.85" />
              <stop offset="55%" stopColor="#60a5fa" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizGalDistA" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#fef9c3" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#fb923c" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#7c2d12" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizGalDistB" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#dbeafe" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="wizGalDistC" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#fae8ff" stopOpacity="0.85" />
              <stop offset="50%" stopColor="#c084fc" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#581c87" stopOpacity="0" />
            </radialGradient>
          </defs>

          <g clipPath="url(#wizardCosmosClip)">
            <g className="wizard-cosmos-tilt">
              <g className="wizard-nebula">
                <circle className="wizard-neb wizard-neb-1" cx="60"  cy="80"  r="58" fill="url(#wizNebA)" />
                <circle className="wizard-neb wizard-neb-2" cx="180" cy="160" r="62" fill="url(#wizNebB)" />
                <circle className="wizard-neb wizard-neb-3" cx="170" cy="55"  r="42" fill="url(#wizNebC)" />
                <circle className="wizard-neb wizard-neb-4" cx="55"  cy="180" r="48" fill="url(#wizNebD)" />
              </g>

              <g className="wizard-stars-bg">
                <circle cx="40"  cy="30"  r="0.5" />
                <circle cx="80"  cy="15"  r="0.4" />
                <circle cx="140" cy="20"  r="0.6" />
                <circle cx="180" cy="15"  r="0.4" />
                <circle cx="210" cy="40"  r="0.5" />
                <circle cx="225" cy="75"  r="0.4" />
                <circle cx="220" cy="120" r="0.6" />
                <circle cx="225" cy="165" r="0.5" />
                <circle cx="210" cy="205" r="0.4" />
                <circle cx="180" cy="225" r="0.6" />
                <circle cx="135" cy="230" r="0.5" />
                <circle cx="90"  cy="225" r="0.4" />
                <circle cx="50"  cy="210" r="0.5" />
                <circle cx="25"  cy="170" r="0.6" />
                <circle cx="15"  cy="130" r="0.4" />
                <circle cx="10"  cy="90"  r="0.5" />
                <circle cx="20"  cy="55"  r="0.4" />
                <circle cx="55"  cy="90"  r="0.5" />
                <circle cx="85"  cy="55"  r="0.4" />
                <circle cx="165" cy="55"  r="0.5" />
                <circle cx="200" cy="90"  r="0.4" />
                <circle cx="195" cy="165" r="0.5" />
                <circle cx="170" cy="200" r="0.4" />
                <circle cx="85"  cy="195" r="0.5" />
                <circle cx="45"  cy="140" r="0.4" />
                <circle cx="115" cy="35"  r="0.5" />
                <circle cx="60"  cy="125" r="0.4" />
                <circle cx="190" cy="50"  r="0.6" />
                <circle cx="155" cy="215" r="0.4" />
                <circle cx="105" cy="210" r="0.5" />
              </g>

              <g className="wizard-galaxies-far">
                <g className="wizard-gal-far wizard-gal-far-1" transform="translate(60 170) rotate(-25)">
                  <ellipse rx="14" ry="3.5" fill="url(#wizGalDistB)" />
                  <ellipse rx="6"  ry="1.5" fill="#dbeafe" opacity="0.85" />
                </g>
                <g className="wizard-gal-far wizard-gal-far-2" transform="translate(195 95) rotate(50)">
                  <ellipse rx="13" ry="3" fill="url(#wizGalDistC)" />
                  <ellipse rx="5"  ry="1.4" fill="#fae8ff" opacity="0.8" />
                </g>
                <g className="wizard-gal-far wizard-gal-far-3" transform="translate(38 65) rotate(15)">
                  <ellipse rx="11" ry="3" fill="url(#wizGalDistA)" />
                  <ellipse rx="4"  ry="1.2" fill="#fef9c3" opacity="0.85" />
                </g>
                <g className="wizard-gal-far wizard-gal-far-4" transform="translate(180 200) rotate(-55)">
                  <ellipse rx="12" ry="3" fill="url(#wizGalDistB)" />
                  <ellipse rx="5"  ry="1.4" fill="#dbeafe" opacity="0.8" />
                </g>
              </g>

              <g className="wizard-stars-mid">
                <circle cx="60"  cy="75"  r="0.9" />
                <circle cx="95"  cy="40"  r="0.8" />
                <circle cx="150" cy="35"  r="1.0" />
                <circle cx="190" cy="75"  r="0.9" />
                <circle cx="195" cy="135" r="0.8" />
                <circle cx="180" cy="180" r="0.9" />
                <circle cx="135" cy="200" r="0.8" />
                <circle cx="80"  cy="170" r="1.0" />
                <circle cx="40"  cy="130" r="0.9" />
                <circle cx="45"  cy="95"  r="0.8" />
                <circle cx="110" cy="75"  r="0.9" />
                <circle cx="160" cy="80"  r="0.8" />
                <circle cx="165" cy="130" r="1.0" />
                <circle cx="155" cy="170" r="0.9" />
                <circle cx="110" cy="180" r="0.8" />
                <circle cx="75"  cy="140" r="0.9" />
                <circle cx="95"  cy="115" r="0.8" />
                <circle cx="140" cy="105" r="0.9" />
                <circle cx="105" cy="140" r="0.8" />
                <circle cx="155" cy="115" r="0.9" />
                <circle cx="75"  cy="105" r="0.8" />
                <circle cx="170" cy="150" r="0.9" />
              </g>

              <g className="wizard-galaxy-center">
                <ellipse cx="120" cy="120" rx="60" ry="14" fill="url(#wizGalCenter)" opacity="0.85" />
                <ellipse cx="120" cy="120" rx="34" ry="9"  fill="url(#wizGalCenter)" opacity="0.95" />
                <ellipse cx="120" cy="120" rx="14" ry="14" fill="url(#wizGalCenter)" />
                <circle  cx="120" cy="120" r="3.2" fill="#ffffff" className="wizard-galaxy-nucleus" />
                <circle cx="155" cy="118" r="0.9" fill="#dbeafe" opacity="0.9" />
                <circle cx="170" cy="121" r="0.7" fill="#bfdbfe" opacity="0.7" />
                <circle cx="85"  cy="121" r="0.9" fill="#dbeafe" opacity="0.9" />
                <circle cx="70"  cy="119" r="0.7" fill="#bfdbfe" opacity="0.7" />
                <circle cx="140" cy="115" r="0.6" fill="#fef3c7" opacity="0.7" />
                <circle cx="100" cy="124" r="0.6" fill="#fef3c7" opacity="0.7" />
              </g>

              <g className="wizard-stars-fg">
                <circle className="wizard-star-fg wizard-star-warm" cx="120" cy="55"  r="1.2" />
                <circle className="wizard-star-fg"                  cx="195" cy="115" r="1.4" />
                <circle className="wizard-star-fg wizard-star-blue" cx="175" cy="165" r="1.1" />
                <circle className="wizard-star-fg"                  cx="125" cy="195" r="1.3" />
                <circle className="wizard-star-fg wizard-star-warm" cx="60"  cy="175" r="1.2" />
                <circle className="wizard-star-fg"                  cx="35"  cy="110" r="1.4" />
                <circle className="wizard-star-fg wizard-star-blue" cx="55"  cy="55"  r="1.1" />
                <circle className="wizard-star-fg"                  cx="180" cy="90"  r="1.3" />
                <circle className="wizard-star-fg wizard-star-warm" cx="85"  cy="155" r="1.0" />
                <circle className="wizard-star-fg"                  cx="170" cy="55"  r="1.2" />
                <circle className="wizard-star-fg wizard-star-blue" cx="50"  cy="140" r="1.0" />
                <circle className="wizard-star-fg"                  cx="200" cy="155" r="1.3" />
              </g>
            </g>
          </g>
        </svg>
        <div className="wizard-orb3d-shine" />
        <div className="wizard-orb3d-rim" />
      </div>

      <div className="wizard-copy">
        <div className="wizard-eyebrow">
          {t("results.wizardTitle", {
            defaultValue: "First-time setup assistant",
          })}
        </div>
        <h2 className="wizard-heading">
          {t("results.wizardBody", {
            defaultValue:
              "Let DEEP AI guide you through creating your first database and ingesting documents.",
          })}
        </h2>
      </div>

      {wizardStep && (
        <div
          className="wizard-chat"
          style={{ display: "flex", flexDirection: "column" }}
        >
          {wizardMessages.map((m, idx) => (
            <div
              key={m.id || idx}
              className={`wizard-msg ${
                m.role === "ai" ? "wizard-msg-ai" : "wizard-msg-user"
              }`}
            >
              {m.text}
            </div>
          ))}
        </div>
      )}

      {wizardStep && (
        <form className="wizard-form" onSubmit={onSubmit}>
          <label className="wizard-form-label">
            {t("results.wizardInputLabel", {
              defaultValue:
                wizardStep === "create-db-name"
                  ? "Type a name for the database, or ask a question."
                  : wizardStep === "create-db-confirm"
                  ? "Confirm the name, or ask a question."
                  : "Add a description, say 'skip', or ask a question.",
            })}
            <input
              className="wizard-form-input"
              type="text"
              value={wizardInput}
              onChange={onWizardInputChange}
              placeholder={t("results.wizardInputPlaceholder", {
                defaultValue:
                  wizardStep === "create-db-name"
                    ? "For example: 'Invoices_EU' or 'Can I rename this later?'"
                    : wizardStep === "create-db-confirm"
                    ? "For example: 'yes', 'no', or a question."
                    : "For example: 'Invoices for EU entities…' or 'skip'",
              })}
            />
          </label>
          {wizardDbError && (
            <div className="wizard-error">{wizardDbError}</div>
          )}
          {assistantError && (
            <div className="wizard-error">{assistantError}</div>
          )}
          <div className="wizard-form-actions">
            {wizardStep === "create-db-confirm" && (
              <>
                <button
                  type="button"
                  className="chip-btn"
                  onClick={onConfirmYes}
                >
                  {t("results.wizardConfirmYes", {
                    defaultValue: "Looks good",
                  })}
                </button>
                <button
                  type="button"
                  className="link-btn"
                  onClick={onConfirmChange}
                >
                  {t("results.wizardConfirmChange", {
                    defaultValue: "Change name",
                  })}
                </button>
              </>
            )}
            {wizardStep === "create-db-desc" && (
              <button
                type="button"
                className="link-btn"
                onClick={onSkipDesc}
              >
                {t("results.wizardSkipDesc", {
                  defaultValue: "Skip description",
                })}
              </button>
            )}
            <button
              type="submit"
              className="chip-btn"
              disabled={assistantLoading || !wizardInput.trim()}
            >
              {assistantLoading && aiEnabled
                ? t("results.wizardAskAssistantLoading", {
                    defaultValue: "Thinking…",
                  })
                : t("results.wizardInputCta", {
                    defaultValue: "Send",
                  })}
            </button>
          </div>
        </form>
      )}

      {!wizardStep && (
        <div className="wizard-steps">
          <div className="wizard-step-card">
            <div className="wizard-step-label">1</div>
            <div className="wizard-step-body">
              <div className="wizard-step-title">
                {t("results.wizardStep1Title", {
                  defaultValue: "Create a database & indexes",
                })}
              </div>
              <div className="wizard-step-text">
                {t("results.wizardStep1Body", {
                  defaultValue:
                    "Define your first database and choose which fields should be searchable.",
                })}
              </div>
              <button
                type="button"
                className="chip-btn"
                onClick={onStartWizard}
              >
                {t("results.wizardStep1Cta", {
                  defaultValue: "Create database",
                })}
              </button>
            </div>
          </div>

          <div className="wizard-step-card">
            <div className="wizard-step-label">2</div>
            <div className="wizard-step-body">
              <div className="wizard-step-title">
                {t("results.wizardStep2Title", {
                  defaultValue: "Upload or ingest documents",
                })}
              </div>
              <div className="wizard-step-text">
                {t("results.wizardStep2Body", {
                  defaultValue:
                    "Send sample files into the new database so DEEP AI has something to explore.",
                })}
              </div>
              <Link to="/upload" className="chip-btn">
                {t("results.wizardStep2Cta", {
                  defaultValue: "Go to Upload",
                })}
              </Link>
            </div>
          </div>
        </div>
      )}

      {wizardStep === "create-db-name" && <></>}
      {wizardStep === "create-db-confirm" && <div className="wizard-form" />}
      {wizardStep === "create-db-desc" && <></>}

      {wizardStep && (
        <div className="wizard-cancel-row">
          {wizardDbName.trim() && (
            <button
              type="button"
              className="chip-btn"
              onClick={onCreateNow}
              disabled={creatingDb}
            >
              {creatingDb
                ? t("results.wizardCreatingDb", {
                    defaultValue: "Creating database...",
                  })
                : t("results.wizardCreateDbCta", {
                    defaultValue: "Create database now",
                  })}
            </button>
          )}
          <button type="button" className="link-btn" onClick={onCancel}>
            {t("results.wizardCancel", {
              defaultValue: "Cancel setup",
            })}
          </button>
        </div>
      )}

      {wizardFlag && (
        <div className="wizard-debug-note">
          {t("results.wizardDebugNote", {
            defaultValue: "Wizard forced by ?wizard=1 (development only).",
          })}
        </div>
      )}
    </div>
  );
}
